import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type {
  CallMediaHealth, HealthMap, RoomState, ServerMessage, TimelineEntry, TopicInfo, TranscriptLine
} from '@besedka/shared';
import { formatTimecode, formatMs, HEARTBEAT_INTERVAL_MS } from '@besedka/shared';
import { api, ApiError } from '../lib/api.js';
import { navigate } from '../lib/router.js';
import { WsClient } from '../lib/ws.js';
import {
  DisconnectReason, SPEAKER_MIC_CONSTRAINTS, connectAsListener, connectAsSpeaker, resumePlayback,
  setCallAudioSession,
  type LivekitHandle,
  type WebrtcPeerHealth
} from '../lib/livekit.js';
import type { LocalAudioTrack } from 'livekit-client';
import * as micDevice from '../lib/micDevice.js';
import { MicPicker } from '../components/MicPicker.js';
import { recordingController, type RecordingUiState } from '../recording/controller.js';

type Tab = 'current' | 'archive' | 'invites';

interface JoinInfo {
  participantId: number;
  episodeId: number;
  sfuIdentity: string;
  listener?: boolean;
  livekitUrl: string;
  livekitToken: string;
}

interface ScreenWakeLockSentinel {
  released?: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

const isChrome = /Chrome\//.test(navigator.userAgent) && !/Edg|OPR/.test(navigator.userAgent);

declare const __BUILD_TS__: string;

/** Короткий локальный бип «вы говорите в мьюте» — только в наушники, не в запись. */
function muteBeep(): void {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.value = 0.12;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
    osc.onended = () => void ctx.close();
  } catch {
    /* звука нет — не страшно */
  }
}

export function RoomPage() {
  const [state, setState] = useState<RoomState | null>(null);
  const [health, setHealth] = useState<HealthMap>({});
  const [speakers, setSpeakers] = useState<string[]>([]);
  const [wsOnline, setWsOnline] = useState(false);
  const [recUi, setRecUi] = useState<RecordingUiState>(recordingController.uiState());
  const [tab, setTab] = useState<Tab>('current');
  const [toast, setToast] = useState('');
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [webrtcState, setWebrtcState] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [peerWebrtc, setPeerWebrtc] = useState<Record<string, WebrtcPeerHealth>>({});
  const [peerMuted, setPeerMuted] = useState<Record<string, boolean>>({});
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [micMuted, setMicMuted] = useState(false);
  const [lkListener, setLkListener] = useState(false);
  const micTrackRef = useRef<LocalAudioTrack | null>(null);
  const takeMicRef = useRef<(() => void) | null>(null);
  const localCallHealthRef = useRef<CallMediaHealth | undefined>(undefined);

  const wsRef = useRef<WsClient | null>(null);
  const hadPendingRef = useRef(false);
  if (recUi.pendingChunks > 0 || recUi.active) hadPendingRef.current = true;
  const joinRef = useRef<JoinInfo | null>(null);
  const stateRef = useRef<RoomState | null>(null);
  const livekitRef = useRef<LivekitHandle | null>(null);
  const wakeLockRef = useRef<ScreenWakeLockSentinel | null>(null);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 6000);
  };

  const feedController = () => {
    if (stateRef.current) {
      recordingController.onRoomState(stateRef.current, joinRef.current?.participantId ?? null);
    }
  };

  useEffect(() => {
    let disposed = false;

    void api<{ me: unknown }>('/api/me').then((data) => {
      if (!data.me) navigate('/login');
    });

    const ws = new WsClient();
    wsRef.current = ws;
    const offMessage = ws.onMessage((message: ServerMessage) => {
      if (message.type === 'state.update') {
        stateRef.current = message.state;
        setState(message.state);
        feedController();
      } else if (message.type === 'health.update') {
        setHealth(message.health);
      } else if (message.type === 'transcript.append') {
        if (message.episodeId === stateRef.current?.episode.id) {
          setTranscript((prev) => mergeTranscript(prev, message.lines));
        }
      } else if (message.type === 'error') {
        showToast(message.message);
      }
    });
    const offStatus = ws.onStatus(setWsOnline);

    recordingController.sendHealth = (report) => ws.send({ type: 'health.report', report });
    recordingController.getWebrtcState = () =>
      livekitRef.current ? webrtcStateRef.current : 'off';
    recordingController.getCallHealth = () => localCallHealthRef.current;
    // Имя микрофона вне записи — от LiveKit-трека (recorder вне live молчит)
    recordingController.getMicLabel = () => micTrackRef.current?.mediaStreamTrack.label ?? '';
    const offRec = recordingController.subscribe(setRecUi);

    // LiveKit: подключаемся сразу — «все слышат всех до записи».
    // Micro-обрывы сигналинга/медиа SDK чинит САМ (resume, трек сохраняется) —
    // мы передозваниваемся со свежим токеном только по ОКОНЧАТЕЛЬНОМУ разрыву
    // (RoomEvent.Disconnected). Свой disconnect от чужого отличаем поколением.
    let lkRetryTimer: number | null = null;
    let lkGen = 0;
    const scheduleLkReconnect = (delayMs: number, asListener: boolean) => {
      if (disposed || lkRetryTimer !== null) return;
      lkRetryTimer = window.setTimeout(() => {
        lkRetryTimer = null;
        void connectLk(asListener);
      }, delayMs);
    };
    const connectLk = async (asListener = false) => {
      const myGen = ++lkGen;
      livekitRef.current?.disconnect();
      livekitRef.current = null;
      setPeerWebrtc({});
      setPeerMuted({});
      try {
        const join = await api<JoinInfo>('/api/join', { method: 'POST', body: { listen: asListener } });
        if (disposed || myGen !== lkGen) return;
        joinRef.current = join;
        feedController();
        const callbacks = {
          onSpeakers: setSpeakers,
          onConnectionState: (s: 'connected' | 'connecting' | 'disconnected') => {
            if (myGen !== lkGen) return;
            webrtcStateRef.current = s;
            setWebrtcState(s);
          },
          onAudioPlaybackStatus: (playing: boolean) => {
            if (myGen !== lkGen) return;
            setAudioBlocked(!playing);
          },
          onParticipantWebrtc: (identity: string, probe: WebrtcPeerHealth) => {
            if (myGen !== lkGen) return;
            // У listen-only вкладки identity имеет служебный суффикс, но в
            // продуктовой панели это всё тот же человек.
            const panelIdentity = identity.startsWith(`${join.sfuIdentity}-view-`)
              ? join.sfuIdentity
              : identity;
            if (!asListener && identity === join.sfuIdentity) {
              const nextCallHealth: CallMediaHealth = {
                capture: probe.captureState ?? probe.state,
                captureStallMs: probe.captureStallMs,
                sfuRttMs: probe.metrics?.rttMs
              };
              const callStateChanged = localCallHealthRef.current?.capture !== nextCallHealth.capture;
              localCallHealthRef.current = nextCallHealth;
              if (callStateChanged) recordingController.reportHealthNow();
            }
            setPeerWebrtc((previous) => {
              const current = previous[panelIdentity];
              if (
                current?.state === probe.state && current.detail === probe.detail &&
                current.captureState === probe.captureState &&
                JSON.stringify(current.metrics) === JSON.stringify(probe.metrics)
              ) return previous;
              return { ...previous, [panelIdentity]: probe };
            });
          },
          onParticipantMuted: (identity: string, muted: boolean) => {
            if (myGen !== lkGen) return;
            const panelIdentity = identity.startsWith(`${join.sfuIdentity}-view-`)
              ? join.sfuIdentity
              : identity;
            setPeerMuted((previous) => previous[panelIdentity] === muted
              ? previous
              : { ...previous, [panelIdentity]: muted });
          },
          onDisconnected: (reason?: DisconnectReason) => {
            if (disposed || myGen !== lkGen) return;
            if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
              // Микрофон перехватила другая вкладка/устройство — уходим в слушатели.
              showToast('Микрофон активен в другой вкладке или на другом устройстве — здесь только прослушивание');
              scheduleLkReconnect(500, true);
            } else {
              scheduleLkReconnect(3000, asListener);
            }
          }
        };
        const handle = asListener
          ? await connectAsListener(join.livekitUrl, join.livekitToken, callbacks)
          : await connectAsSpeaker(
              join.livekitUrl, join.livekitToken, callbacks, micTrackRef.current,
              micDevice.getPreferredDeviceId()
            );
        if (disposed || myGen !== lkGen) { handle.disconnect(); return; }
        livekitRef.current = handle;
        if (handle.micTrack) micTrackRef.current = handle.micTrack;
        setLkListener(asListener);
        // Слушатель: как только владелец нашей identity ушёл из комнаты
        // (зомби-вкладка сдохла / другое устройство вышло) — забираем микрофон сами.
        if (asListener) {
          const reclaimId = window.setInterval(() => {
            if (disposed || myGen !== lkGen) { window.clearInterval(reclaimId); return; }
            const holder = handle.room.remoteParticipants.get(join.sfuIdentity);
            if (!holder) {
              window.clearInterval(reclaimId);
              showToast('Микрофон снова свободен — включаю его в этой вкладке');
              void connectLk(false);
            }
          }, 5000);
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          navigate('/login');
          return;
        }
        showToast(`Голосовая связь недоступна: ${(error as Error).message} — повтор через 10 с`);
        scheduleLkReconnect(10_000, asListener);
      }
    };
    takeMicRef.current = () => void connectLk(false);
    // Микрофон прошлого эфира применяется однократно ДО первого захвата;
    // явный выбор пользователя (localStorage) всегда важнее автоматики.
    void micDevice
      .resolveAutoOnce()
      .then((auto) => {
        if (disposed) return;
        if (auto?.kind === 'applied') showToast(`Микрофон прошлого эфира: ${auto.device.label}`);
        else if (auto?.kind === 'missing') showToast(`Прошлый микрофон «${auto.label}» не найден — используется системный`);
      })
      .catch(() => {})
      .then(() => {
        if (!disposed) void connectLk();
      });

    return () => {
      disposed = true;
      if (lkRetryTimer !== null) window.clearTimeout(lkRetryTimer);
      offMessage();
      offStatus();
      offRec();
      ws.close();
      livekitRef.current?.disconnect();
      micTrackRef.current?.stop();
      setCallAudioSession(false);
      recordingController.dispose();
    };
  }, []);

  // Общий мьют: LiveKit-трек (эфир) + raw-запись (нули в дорожку).
  const toggleMute = async () => {
    const next = !micMuted;
    recordingController.setMuted(next);
    const identity = joinRef.current?.sfuIdentity;
    if (identity) setPeerMuted((previous) => ({ ...previous, [identity]: next }));
    const track = micTrackRef.current;
    if (track) {
      if (next) await track.mute();
      else await track.unmute();
    }
    setMicMuted(next);
  };

  // Смена микрофона: LiveKit-трек перезапускается на месте (публикация и mute
  // сохраняются), raw-рекордер переоткрывает сегмент (~1-2 с тишины на стыке).
  // Ошибки двух пайплайнов независимы — полусмена видна по имени в карточке.
  const switchMicDevice = async (choice: 'default' | { deviceId: string; label: string }) => {
    micDevice.select(choice);
    const track = micTrackRef.current;
    if (track) {
      try {
        await track.restartTrack(
          choice === 'default'
            ? { ...SPEAKER_MIC_CONSTRAINTS }
            : { ...SPEAKER_MIC_CONSTRAINTS, deviceId: { exact: choice.deviceId } }
        );
        if (micMuted) await track.mute();
      } catch {
        showToast('Не удалось переключить микрофон эфира — устройство недоступно');
      }
    }
    try {
      await recordingController.switchMic();
    } catch {
      /* рекордер сам ретраит и показывает lastError */
    }
    recordingController.reportHealthNow();
  };

  // TeamSpeak-стайл: говоришь в мьюте → локальный бип (никуда не пишется) + плашка
  const [muteTalking, setMuteTalking] = useState(false);
  const lastBeepRef = useRef(0);
  useEffect(() => {
    if (!micMuted) {
      setMuteTalking(false);
      return;
    }
    const id = window.setInterval(() => {
      const talking = recordingController.uiState().level > 0.02;
      setMuteTalking(talking);
      if (talking && Date.now() - lastBeepRef.current > 4000) {
        lastBeepRef.current = Date.now();
        muteBeep();
      }
    }, 700);
    return () => window.clearInterval(id);
  }, [micMuted]);

  const webrtcStateRef = useRef<'connected' | 'connecting' | 'disconnected'>('connecting');

  // Держим экран включённым всё время звонка. При ручной блокировке ОС снимет
  // screen lock, но AudioSession остаётся play-and-record; после разблокировки
  // повторно получаем lock, потому что старый sentinel уже недействителен.
  const keepScreenAwake = webrtcState !== 'disconnected' || recUi.active;
  useEffect(() => {
    let disposed = false;
    const wakeLock = (navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<ScreenWakeLockSentinel> };
    }).wakeLock;

    const acquire = async (): Promise<void> => {
      if (!keepScreenAwake || !wakeLock || document.visibilityState !== 'visible') return;
      if (wakeLockRef.current && !wakeLockRef.current.released) return;
      try {
        const lock = await wakeLock.request('screen');
        if (disposed) {
          await lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener('release', () => {
          if (wakeLockRef.current === lock) wakeLockRef.current = null;
        });
      } catch {
        // Low Power Mode и политика браузера могут отказать — звонок при этом
        // продолжает работать через AudioSession/WebRTC.
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    if (keepScreenAwake) {
      document.addEventListener('visibilitychange', onVisibilityChange);
      void acquire();
    }

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      if (lock && !lock.released) void lock.release().catch(() => {});
    };
  }, [keepScreenAwake]);

  // Детект новой сборки: сравниваем ETag корня; изменился — просим перезагрузить.
  // (Открытая вкладка сама не обновляется — фиксы иначе не доезжают до клиента.)
  useEffect(() => {
    let baseEtag: string | null = null;
    let notified = false;
    const check = async () => {
      try {
        const res = await fetch('/', { method: 'HEAD', cache: 'no-store' });
        const etag = res.headers.get('etag');
        if (!etag) return;
        if (baseEtag === null) baseEtag = etag;
        else if (etag !== baseEtag && !notified) {
          notified = true;
          showToast('Вышло обновление Besedka — перезагрузите страницу (Cmd/Ctrl+Shift+R)');
        }
      } catch { /* офлайн — не страшно */ }
    };
    void check();
    const id = window.setInterval(() => void check(), 60_000);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    return () => { window.clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  // Первичная загрузка стенограммы по REST при смене выпуска; далее live-приём по WS
  const episodeId = state?.episode.id;
  useEffect(() => {
    if (!episodeId) return;
    let cancelled = false;
    setTranscript([]);
    void api<{ lines: TranscriptLine[] }>(`/api/episodes/${episodeId}/transcript?limit=50`)
      .then((data) => { if (!cancelled) setTranscript(data.lines); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [episodeId]);

  const send = (message: Parameters<WsClient['send']>[0]) => wsRef.current?.send(message);

  if (!state) {
    return (
      <main class="shell">
        <p class="mutedText">Загрузка…</p>
      </main>
    );
  }

  const me = state.me;
  const isUser = me?.kind === 'user';
  const episode = state.episode;
  const live = episode.status === 'live';

  return (
    <main class="shell">
      <header class="topbar">
        <div>
          {isUser && (
            <nav class="tabs">
              <button class={tab === 'current' ? 'active' : ''} onClick={() => setTab('current')}>Текущий</button>
              <button class={tab === 'archive' ? 'active' : ''} onClick={() => setTab('archive')}>Архив</button>
              <button class={tab === 'invites' ? 'active' : ''} onClick={() => setTab('invites')}>Invites</button>
            </nav>
          )}
          <p class="eyebrow">{state.podcast.title}</p>
          <h1>{episode.title}</h1>
        </div>
        <div class="userbar">
          {!wsOnline && <span class="recState bad">нет связи с сервером</span>}
          <strong>{me?.displayName}</strong>
          <button
            onClick={() =>
              void api('/api/auth/logout', { method: 'POST' }).then(() => navigate('/login'))
            }
          >
            Выйти
          </button>
        </div>
      </header>

      {!isChrome && (
        <div class="banner warn">
          Полностью поддерживается Chrome. В этом браузере запись работает, но возможны ограничения
          (эхоподавление, фоновые вкладки). Наушники обязательны.
        </div>
      )}
      {audioBlocked && (
        <div class="banner info">
          Браузер заблокировал звук.{' '}
          <button
            class="small"
            onClick={() => {
              if (livekitRef.current) {
                void resumePlayback(livekitRef.current.room)
                  .then(() => setAudioBlocked(false))
                  .catch((error) => showToast(`Не удалось включить звук: ${(error as Error).message}`));
              }
            }}
          >
            Включить звук
          </button>
        </div>
      )}
      {recUi.lockBusy && (
        <div class="banner warn">
          Запись идёт в другой вкладке. Эта вкладка в режиме просмотра — темы и чат работают.
          Если та вкладка умрёт, запись автоматически продолжится здесь.
        </div>
      )}
      {recUi.lastError && <div class="banner err">{recUi.lastError}</div>}
      {live && recUi.active && recUi.pendingChunks > 3 && (
        <div class="banner warn">
          Аплоад отстаёт: {recUi.pendingChunks} чанков локально (~{Math.round(recUi.uploadLagMs / 1000)} с).
          Запись продолжается в IndexedDB — ничего не теряется.
        </div>
      )}
      {!recUi.active && recUi.pendingChunks > 0 && (
        <div class="banner warn">
          Дозаливка записи: осталось {recUi.pendingChunks} чанков. Не закрывайте страницу.
        </div>
      )}
      {!recUi.active && recUi.pendingChunks === 0 && hadPendingRef.current && (
        <div class="banner info">✓ Запись полностью выгружена на сервер — страницу можно закрывать.</div>
      )}
      {episode.status === 'finished' && (
        <div class="banner info">
          Выпуск завершён: идёт дозаливка и сборка записи. Комната переключится на новый
          выпуск через час — или сразу по кнопке «Новый выпуск».
        </div>
      )}
      {toast && <div class="banner err">{toast}</div>}

      {isUser && (
        <EpisodeActions state={state} send={send} showToast={showToast} />
      )}

      <StatusGrid state={state} recUi={recUi} webrtc={webrtcState} />

      {lkListener ? (
        <div class="banner info">
          🎧 Здесь только прослушивание — микрофон активен в другой вкладке или на другом устройстве.{' '}
          <button onClick={() => takeMicRef.current?.()}>Включить микрофон здесь</button>
        </div>
      ) : (
        <div class="muteRow">
          <button
            class={micMuted ? 'danger' : ''}
            onClick={() => void toggleMute()}
            title="Мьют глушит вас в эфире и в записи (в дорожку пишется тишина той же длительности)"
          >
            {micMuted ? '🔇 Вы в мьюте — включить микрофон' : '🎙 Мьют'}
          </button>
          {muteTalking
            ? <span class="notice">⚠️ Вы говорите в мьюте — вас не слышно и это не пишется!</span>
            : micMuted && <span class="mutedText">эфир и запись глушатся: в дорожке будет тишина</span>}
          <MicPicker
            micTrack={micTrackRef.current}
            recActive={recUi.active}
            recLevel={recUi.level}
            onSelect={(choice) => void switchMicDevice(choice)}
          />
        </div>
      )}

      {tab === 'current' && (
        <div class="roomGrid">
          <section>
            <ParticipantsPanel
              state={state}
              health={health}
              speakers={speakers}
              peerWebrtc={peerWebrtc}
              peerMuted={peerMuted}
              send={send}
              isUser={isUser}
            />
          </section>
          <section>
            <TopicsPanel state={state} send={send} live={live} isUser={isUser} showToast={showToast} />
            <TimelinePanel state={state} send={send} isUser={isUser} />
            <TranscriptPanel lines={transcript} />
          </section>
          <section>
            <ChatPanel state={state} send={send} />
          </section>
        </div>
      )}
      {tab === 'archive' && <ArchivePanel state={state} />}
      {tab === 'invites' && <InvitesPanel />}
      <p class="buildTag">сборка {__BUILD_TS__} UTC</p>
    </main>
  );
}

// Удаление выпуска — в корзину (восстанавливается из архива); обновление прилетит по WS.
// Текущий выпуск при удалении заменяется новым draft'ом с теми же темами.
async function deleteEpisodeConfirmed(id: number, title: string, isCurrent: boolean): Promise<void> {
  const text = isCurrent
    ? `Удалить ${title}? Выпуск с записью уйдёт в корзину (архив → «Корзина»), а темы переедут в новый выпуск с тем же номером.`
    : `Удалить ${title}? Выпуск уйдёт в корзину — восстановить можно в архиве.`;
  if (!confirm(text)) return;
  try {
    await api(`/api/episodes/${id}`, { method: 'DELETE' });
  } catch (error) {
    alert(`Не удалось удалить: ${(error as Error).message}`);
  }
}

async function restoreEpisode(id: number, title: string): Promise<void> {
  if (!confirm(`Восстановить ${title} из корзины?`)) return;
  try {
    await api(`/api/episodes/${id}/restore`, { method: 'POST' });
  } catch (error) {
    alert(`Не удалось восстановить: ${(error as Error).message}`);
  }
}

async function purgeEpisodeConfirmed(id: number, title: string): Promise<void> {
  if (!confirm(`Удалить ${title} навсегда?`)) return;
  if (!confirm(`Точно навсегда? Запись, темы, таймлайн и чат выпуска пропадут безвозвратно.`)) return;
  try {
    await api(`/api/episodes/${id}/purge`, { method: 'POST' });
  } catch (error) {
    alert(`Не удалось удалить: ${(error as Error).message}`);
  }
}

// ---------- Действия над выпуском ----------

function EpisodeActions({
  state, send, showToast
}: {
  state: RoomState;
  send: (m: never) => void;
  showToast: (t: string) => void;
}) {
  const episode = state.episode;
  const sendAny = send as (m: unknown) => void;

  const guestLink = async () => {
    const name = prompt('Имя гостя?', 'Гость');
    if (name === null) return;
    const { url } = await api<{ url: string }>(`/api/episodes/${episode.id}/guest-links`, {
      method: 'POST',
      body: { displayName: name }
    });
    await navigator.clipboard.writeText(url).catch(() => {});
    showToast(`Гостевая ссылка скопирована: ${url}`);
  };

  const obsLinks = async () => {
    const data = await api<{ overlayUrl: string; speakerUrls: { displayName: string; url: string }[] }>(
      '/api/obs/token',
      { method: 'POST', body: { episodeId: episode.id } }
    );
    const text = [
      `Overlay: ${data.overlayUrl}`,
      ...data.speakerUrls.map((s) => `${s.displayName}: ${s.url}`)
    ].join('\n');
    await navigator.clipboard.writeText(text).catch(() => {});
    alert(`OBS-ссылки (скопированы в буфер):\n\n${text}`);
  };

  return (
    <div class="actions">
      {episode.status === 'draft' && (
        <button class="primary" onClick={() => sendAny({ type: 'episode.start' })}>
          ● Начать эфир и запись
        </button>
      )}
      {episode.status === 'live' && (
        <>
          <button
            class="danger"
            onClick={() => {
              if (confirm('Завершить эфир? Запись остановится у всех.')) sendAny({ type: 'episode.finish' });
            }}
          >
            ■ Завершить эфир
          </button>
          <button
            title="Эфир начался неудачно: завершить его, убрать в корзину и открыть тот же выпуск заново с теми же темами"
            onClick={() => {
              if (confirm(`Начать ${episode.title} заново? Текущая запись остановится и уйдёт в корзину, темы останутся — откроется новый ${episode.title}.`)) {
                sendAny({ type: 'episode.restart' });
              }
            }}
          >
            ↺ Начать заново
          </button>
        </>
      )}
      {episode.status === 'finished' && (
        <>
          <a
            class="buttonLink"
            href={`/episodes/${episode.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/episodes/${episode.id}`);
            }}
          >
            Страница выпуска
          </a>
          <button
            class="primary"
            onClick={() => {
              if (confirm('Начать новый выпуск? Завершённый уйдёт в архив.')) sendAny({ type: 'episode.new' });
            }}
          >
            Новый выпуск
          </button>
          <button
            class="danger"
            title="В корзину; темы переедут в новый выпуск с тем же номером"
            onClick={() => void deleteEpisodeConfirmed(episode.id, episode.title, episode.id === state.activeEpisodeId)}
          >
            Удалить выпуск
          </button>
        </>
      )}
      <button onClick={() => void guestLink()}>Гостевая ссылка</button>
      <button onClick={() => void obsLinks()}>OBS</button>
      <a class="buttonLink" href={`/api/episodes/${episode.id}/export/markdown`} target="_blank" rel="noreferrer">
        Markdown
      </a>
      <a class="buttonLink" href={`/api/episodes/${episode.id}/export/youtube`} target="_blank" rel="noreferrer">
        YouTube
      </a>
      {episode.status !== 'finished' && (
        <label class="granularity">
          <span>Дробление тем</span>
          <span class="ends">только ручные</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={String(episode.granularity)}
            onChange={(e) => sendAny({ type: 'episode.setGranularity', value: Number(e.currentTarget.value) })}
          />
          <span class="ends">ad-hoc ~3 мин</span>
        </label>
      )}
    </div>
  );
}

// ---------- Статус ----------

function StatusGrid({
  state, recUi, webrtc
}: {
  state: RoomState;
  recUi: RecordingUiState;
  webrtc: string;
}) {
  const episode = state.episode;
  const [, forceTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => forceTick((v) => v + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const elapsed =
    episode.status === 'live' && episode.epochTs
      ? formatTimecode((Date.now() - episode.epochTs) / 1000)
      : episode.status === 'finished' && episode.epochTs && episode.finishedAt
        ? formatTimecode((new Date(episode.finishedAt).getTime() - episode.epochTs) / 1000)
        : '00:00:00';

  const recording = recUi.active
    ? recUi.pendingChunks > 3
      ? `отстаёт на ${Math.round(recUi.uploadLagMs / 1000)} с`
      : 'ок'
    : recUi.lockBusy
      ? 'в другой вкладке'
      : recUi.pendingChunks > 0
        ? `дозаливка: ${recUi.pendingChunks}`
        : '—';

  return (
    <section class="statusGrid">
      <div>
        <span>Эфир</span>
        <strong class={episode.status === 'live' ? 'live' : ''}>
          {episode.status === 'live' ? 'LIVE' : episode.status === 'finished' ? 'Завершён' : 'OFF'}
        </strong>
        <em>{elapsed}</em>
      </div>
      <div>
        <span>Запись</span>
        <strong>{recording}</strong>
        <em>{recUi.active ? `сегмент ${recUi.segmentNo ?? '…'} · ${recUi.sampleRate} Гц` : ''}</em>
      </div>
      <div>
        <span>Связь</span>
        <strong>{webrtc === 'connected' ? 'ок' : webrtc === 'connecting' ? '…' : 'нет'}</strong>
        <em>WebRTC</em>
      </div>
      <div>
        <span>Темы</span>
        <strong>{state.topics.length}</strong>
      </div>
    </section>
  );
}

// ---------- Участники + здоровье ----------

function ParticipantsPanel({
  state, health, speakers, peerWebrtc, peerMuted, send, isUser
}: {
  state: RoomState;
  health: HealthMap;
  speakers: string[];
  peerWebrtc: Record<string, WebrtcPeerHealth>;
  peerMuted: Record<string, boolean>;
  send: (m: never) => void;
  isUser: boolean;
}) {
  const sendAny = send as (m: unknown) => void;
  // health.at сам по себе не вызывает render. Без собственного таймера
  // протухший heartbeat мог оставаться зелёным до минуты — пока не менялось
  // что-нибудь ещё на странице.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 300);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div class="panel">
      <h2>Участники</h2>
      {state.participants.length === 0 && <p class="mutedText">Пока никого</p>}
      {state.participants.map((p) => {
        const report = health[String(p.id)];
        const myIdentity = state.me ? `${state.me.kind === 'user' ? 'u' : 'g'}${state.me.id}` : null;
        const isMe = p.sfuIdentity === myIdentity;
        const heartbeatAge = report ? Math.max(0, now - report.at) : Number.POSITIVE_INFINITY;
        // Свою карточку не оцениваем через лишний круг browser→server→browser:
        // именно он давал жёлтые вспышки при нормальном соединении.
        const missedHeartbeats = isMe
          ? 0
          : report
          ? Math.floor(Math.max(0, heartbeatAge - 750) / HEARTBEAT_INTERVAL_MS)
          : 3;
        const heartbeatState: 'healthy' | 'degraded' | 'disconnected' = missedHeartbeats >= 3
          ? 'disconnected'
          : missedHeartbeats >= 1 ? 'degraded' : 'healthy';
        const stale = heartbeatState === 'disconnected';
        const speaking = speakers.includes(p.sfuIdentity);
        const muted = peerMuted[p.sfuIdentity] ?? (report && !stale ? Boolean(report.muted) : false);
        // HFP: Bluetooth-гарнитура в узкополосном режиме (≤16 кГц) необратимо
        // портит raw-запись — предупреждаем, пока эфир можно спасти сменой устройства.
        const micHz = report && !stale ? report.micSampleRate ?? 0 : 0;
        const hfp = report && !stale && report.recording && micHz > 0 && micHz <= 16000;
        const rtc = peerWebrtc[p.sfuIdentity];
        let rtcState = rtc?.state ?? (p.online ? 'degraded' : 'disconnected');
        let rtcTitle = rtc?.detail ?? (p.online
          ? 'ждём состояние аудиотрека LiveKit'
          : 'участник оффлайн');
        const severity = { healthy: 0, degraded: 1, disconnected: 2 } as const;
        const worsenRtc = (next: 'healthy' | 'degraded' | 'disconnected', title: string) => {
          if (severity[next] >= severity[rtcState]) {
            rtcState = next;
            rtcTitle = title;
          }
        };
        if (!isMe && heartbeatState === 'disconnected') {
          rtcState = 'disconnected';
          rtcTitle = 'три heartbeat подряд не получены';
        } else if (!isMe && heartbeatState === 'degraded') {
          worsenRtc('degraded', `${missedHeartbeats} heartbeat не получен${missedHeartbeats === 1 ? '' : 'о'}`);
        }
        if (report?.webrtc === 'disconnected') {
          worsenRtc('disconnected', 'браузер участника сообщает о разрыве WebRTC');
        } else if (report?.webrtc === 'connecting') {
          worsenRtc('degraded', 'браузер участника восстанавливает WebRTC');
        }
        if (!isMe && report?.pageState && report.pageState !== 'visible') {
          const pageStateAge = (report.pageStateAgeMs ?? 0) + heartbeatAge;
          const label = report.pageState === 'frozen' ? 'вкладка заморожена' : 'экран заблокирован или вкладка скрыта';
          worsenRtc(
            pageStateAge >= HEARTBEAT_INTERVAL_MS * 3 ? 'disconnected' : 'degraded',
            pageStateAge >= HEARTBEAT_INTERVAL_MS * 3 ? `${label} больше 6 с` : label
          );
        }
        const capture = report?.call?.capture;
        if (!isMe && capture === 'disconnected') {
          worsenRtc('disconnected', 'processed-микрофон участника не выдаёт семплы');
        } else if (!isMe && capture === 'degraded') {
          worsenRtc('degraded', 'processed-микрофон участника пропускает семплы');
        }
        let rawState: 'healthy' | 'degraded' | 'disconnected' | 'idle';
        let rawTitle: string;
        if (!report) {
          rawState = state.episode.status === 'live' ? 'disconnected' : 'idle';
          rawTitle = state.episode.status === 'live' ? 'raw-телеметрия ещё не получена' : 'raw-запись не запущена';
        } else if (!isMe && heartbeatState === 'disconnected') {
          rawState = 'disconnected';
          rawTitle = 'три heartbeat raw-телеметрии подряд не получены';
        } else if (!isMe && heartbeatState === 'degraded') {
          rawState = 'degraded';
          rawTitle = `${missedHeartbeats} heartbeat raw-телеметрии не получен${missedHeartbeats === 1 ? '' : 'о'}`;
        } else if (state.episode.status !== 'live') {
          rawState = 'idle';
          rawTitle = 'raw-запись не запущена: эфир сейчас не идёт';
        } else if (state.episode.status === 'live' && !report.recording) {
          rawState = 'disconnected';
          rawTitle = 'raw-запись не идёт';
        } else if ((report.rawFrameAgeMs ?? 0) >= HEARTBEAT_INTERVAL_MS * 3) {
          rawState = 'disconnected';
          rawTitle = 'raw-захват не выдаёт PCM-фреймы';
        } else if ((report.rawFrameAgeMs ?? 0) >= HEARTBEAT_INTERVAL_MS) {
          rawState = 'degraded';
          rawTitle = 'raw-захват пропустил PCM-фреймы';
        } else if (report.pendingChunks > 3 || report.uploadLagMs > 10_000) {
          rawState = 'degraded';
          rawTitle = `raw-запись идёт, загрузка отстаёт на ${Math.round(report.uploadLagMs / 1000)} с`;
        } else {
          rawState = 'healthy';
          rawTitle = 'raw-запись, PCM-фреймы и загрузка работают';
        }
        const metrics = rtc?.metrics;
        const remoteRtt = report?.call?.sfuRttMs;
        const approximateLatency = metrics?.rttMs !== undefined && remoteRtt !== undefined
          ? Math.round((metrics.rttMs + remoteRtt) / 2 + (metrics.bufferMs ?? 0))
          : undefined;
        const latency = approximateLatency ?? metrics?.rttMs;
        const integerStat = (value: number | undefined) => value === undefined ? '—' : String(Math.round(value));
        const percentStat = (value: number | undefined) => value === undefined ? '—' : value.toFixed(1);
        let recText = '—';
        let recClass = '';
        if (report && !stale) {
          if (!report.recording) {
            recText = state.episode.status === 'live' ? 'НЕ ПИШЕТ' : 'ожидание';
            recClass = state.episode.status === 'live' ? 'bad' : '';
          } else if (report.pendingChunks > 3) {
            recText = `отстаёт на ${Math.round(report.uploadLagMs / 1000)} с`;
            recClass = 'lag';
          } else {
            recText = 'запись: ок';
          }
        } else if (p.online) {
          recText = 'нет свежих данных (вкладка в фоне?)';
        } else if (state.episode.status === 'live') {
          recText = 'ОФФЛАЙН (пишет локально?)';
          recClass = 'bad';
        } else {
          recText = 'оффлайн';
        }
        return (
          <div class={`participant${speaking ? ' speaking' : ''}`} key={p.id}>
            <div class="row">
              <span class="healthLights" aria-label={`WebRTC: ${rtcTitle}; raw: ${rawTitle}`}>
                <span class={`healthDot rtc ${rtcState}`} title={`WebRTC: ${rtcTitle}`} />
                <span class={`healthDot raw ${rawState}`} title={`Raw-запись: ${rawTitle}`} />
              </span>
              <span class="name">{p.displayName}{p.isGuest ? ' (гость)' : ''}</span>
              {isUser && !state.readOnly && p.isGuest && (
                <button
                  class="plain cardAction"
                  title="Переименовать гостя"
                  onClick={() => {
                    const name = prompt('Имя гостя', p.displayName);
                    if (name?.trim()) sendAny({ type: 'guest.rename', participantId: p.id, displayName: name });
                  }}
                >
                  ✎
                </button>
              )}
              {isUser && !state.readOnly && !p.online && !isMe && (
                <button
                  class="plain cardAction"
                  title="Убрать из списка (только оффлайн и без записанного аудио)"
                  onClick={() => {
                    if (confirm(`Убрать «${p.displayName}» из списка участников?`)) {
                      sendAny({ type: 'participant.remove', participantId: p.id });
                    }
                  }}
                >
                  ✕
                </button>
              )}
              {muted && <span title="В мьюте: в дорожку пишется тишина">🔇</span>}
              {hfp && (
                <span class="hfpWarn" title={`Микрофон захватывается на ${Math.round(micHz / 1000)} кГц (Bluetooth-гарнитура в HFP-режиме). Запись узкополосная — выберите встроенный или проводной микрофон в селекторе рядом с кнопкой мьюта, а гарнитуру используйте только на воспроизведение.`}>
                  ⚠ {Math.round(micHz / 1000)}кГц
                </span>
              )}
              {speaking && <span title="Говорит">🎙</span>}
            </div>
            {report && !stale && report.micLabel && (
              <div class="micLabel" title={report.micLabel}>🎙 {report.micLabel}</div>
            )}
            <div class="levelBar">
              <div style={{ width: `${Math.min(100, (report && !stale ? report.level : 0) * 300)}%` }} />
            </div>
            <div class="callStats" aria-label="Статистика WebRTC">
              <span class="callStat" title={approximateLatency !== undefined ? 'Примерная задержка между участниками' : 'RTT до SFU'}>
                <span class="callStatLabel">lat</span>
                <span class="callStatValue">{integerStat(latency)}</span>
                <span class="callStatUnit">ms</span>
              </span>
              <span class="callStat" title="Буфер воспроизведения">
                <span class="callStatLabel">buf</span>
                <span class="callStatValue">{integerStat(metrics?.bufferMs)}</span>
                <span class="callStatUnit">ms</span>
              </span>
              <span class="callStat" title="Джиттер входящего аудио">
                <span class="callStatLabel">jit</span>
                <span class="callStatValue">{integerStat(metrics?.jitterMs)}</span>
                <span class="callStatUnit">ms</span>
              </span>
              <span class="callStat" title="Потерянные аудиопакеты">
                <span class="callStatLabel">loss</span>
                <span class="callStatValue">{percentStat(metrics?.lossPercent)}</span>
                <span class="callStatUnit">%</span>
              </span>
              <span class="callStat" title="Доля аудио, восстановленного concealment">
                <span class="callStatLabel">cncl</span>
                <span class="callStatValue">{percentStat(metrics?.concealedPercent)}</span>
                <span class="callStatUnit">%</span>
              </span>
              <span class={`callStat${metrics?.catchupActive ? ' catchup' : ''}`} title="Ускоренное воспроизведение догоняет live">
                <span class="callStatLabel">sync</span>
                <span class="callStatValue">{metrics?.catchupActive ? 'on' : '—'}</span>
              </span>
            </div>
            <div class={`recState ${recClass}`}>{recText}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Темы ----------

function TopicsPanel({
  state, send, live, isUser, showToast
}: {
  state: RoomState;
  send: (m: never) => void;
  live: boolean;
  isUser: boolean;
  showToast: (t: string) => void;
}) {
  const sendAny = send as (m: unknown) => void;
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [comment, setComment] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');

  const create = (event: Event) => {
    event.preventDefault();
    if (title.trim().length < 3) return;
    sendAny({ type: 'topic.create', title, url, comment });
    setTitle('');
    setUrl('');
    setComment('');
  };

  const doImport = async (allowDuplicate = false): Promise<void> => {
    if (!importText.trim()) return;
    try {
      const result = await api<{ imported: number }>('/api/topics/import-markdown', {
        method: 'POST',
        body: { markdown: importText, allowDuplicate }
      });
      setImportText('');
      setImportOpen(false);
      showToast(`Импортировано тем: ${result.imported}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        if (confirm('Часть ссылок уже была в прошлых выпусках. Импортировать всё равно?')) {
          return doImport(true);
        }
        return;
      }
      showToast((error as Error).message);
    }
  };

  // Тема, которая сейчас в эфире (открытая запись таймлайна): её кнопка
  // «В эфир» выключена — дубль (как в 37-м выпуске) невозможен и с клиента.
  const onAirTopicId = state.timeline.find((e) => e.endMs === null)?.topicId ?? null;

  // Тема в эфире закреплена наверху списка (sort стабильный — остальной
  // порядок не трогаем). Кликнувшему — FLIP-анимация «переезда наверх»:
  // позиция карточки замеряется при клике, после переранжирования карточка
  // стартует со старого места. У остальных участников — мгновенная перестановка.
  const orderedTopics = onAirTopicId === null
    ? state.topics
    : [...state.topics].sort((a, b) => (a.id === onAirTopicId ? -1 : b.id === onAirTopicId ? 1 : 0));
  const topicEls = useRef(new Map<number, HTMLElement>());
  const pendingFlipRef = useRef<{ id: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const pending = pendingFlipRef.current;
    if (!pending || pending.id !== onAirTopicId) return;
    pendingFlipRef.current = null;
    const el = topicEls.current.get(pending.id);
    if (!el) return;
    const dy = pending.top - el.getBoundingClientRect().top;
    if (Math.abs(dy) < 4) return;
    el.animate(
      [{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
      { duration: 450, easing: 'cubic-bezier(0.22, 0.9, 0.3, 1)' }
    );
  }, [onAirTopicId]);

  return (
    <div class="panel">
      <h2>Темы</h2>
      {!state.readOnly && (
        <form class="topicForm" onSubmit={create}>
          <div class="row">
            <input
              type="text"
              placeholder="Название темы"
              value={title}
              onInput={(e) => setTitle(e.currentTarget.value)}
            />
            <input type="text" placeholder="URL" value={url} onInput={(e) => setUrl(e.currentTarget.value)} />
          </div>
          <div class="row">
            <input
              type="text"
              placeholder="Комментарий (опционально)"
              value={comment}
              onInput={(e) => setComment(e.currentTarget.value)}
            />
            <button class="primary" type="submit" style={{ flex: '0 0 auto' }}>
              Добавить
            </button>
            <button
              type="button"
              style={{ flex: '0 0 auto' }}
              onClick={() => setImportOpen(!importOpen)}
            >
              Импорт
            </button>
          </div>
          {importOpen && (
            <div>
              <textarea
                rows={6}
                placeholder={'Нумерованный Markdown-список:\n1. [Название темы](https://ссылка)\n2. Тема без ссылки'}
                value={importText}
                onInput={(e) => setImportText(e.currentTarget.value)}
              />
              <div class="row" style={{ marginTop: '6px' }}>
                <button class="primary" type="button" onClick={() => void doImport()}>
                  Импортировать
                </button>
              </div>
            </div>
          )}
        </form>
      )}
      {state.topics.length === 0 && <p class="mutedText">Тем пока нет</p>}
      {orderedTopics.map((topic: TopicInfo) => (
        <div
          class={`topic status-${topic.status}${topic.id === onAirTopicId ? ' onAir' : ''}`}
          key={topic.id}
          ref={(el) => {
            if (el) topicEls.current.set(topic.id, el);
            else topicEls.current.delete(topic.id);
          }}
        >
          {isUser && !state.readOnly && (
            <button
              class={`voteBtn${topic.myVote ? ' my' : ''}`}
              title={topic.voters.join(', ')}
              onClick={() => sendAny({ type: 'topic.vote', topicId: topic.id, vote: !topic.myVote })}
            >
              ▲<small>{topic.voteCount}</small>
            </button>
          )}
          <div class="body">
            <div class="title">
              {topic.url ? (
                <a href={topic.url} target="_blank" rel="noreferrer">
                  {topic.title}
                </a>
              ) : (
                topic.title
              )}
            </div>
            <div class="meta">
              {topic.author} · {topic.status}
              {topic.isAdhoc && <span class="badge auto">авто</span>}
              {topic.voters.length > 0 && ` · голоса: ${topic.voters.join(', ')}`}
            </div>
            {topic.comment && <div class="comment">{topic.comment}</div>}
            {topic.summaryStatus === 'pending' && <div class="articleSummary pending">Готовим recap статьи…</div>}
            {topic.summaryStatus === 'ready' && (
              <div class="articleSummary">
                <span class="summaryLabel">
                  Recap · {topic.summaryProvider === 'local' ? 'локально' : 'Gemini'}
                </span>{' '}
                {topic.articleSummary}
              </div>
            )}
            {topic.summaryStatus === 'failed' && (
              <div class="articleSummary failed">Recap: {topic.summaryError}</div>
            )}
          </div>
          {!state.readOnly && (
            <div class="topicActions">
              {live && isUser && (
                <button
                  class="small primary"
                  disabled={topic.id === onAirTopicId}
                  title={topic.id === onAirTopicId ? 'Тема уже в эфире' : 'Выбрать тему в эфире'}
                  onClick={() => {
                    const el = topicEls.current.get(topic.id);
                    if (el) pendingFlipRef.current = { id: topic.id, top: el.getBoundingClientRect().top };
                    sendAny({ type: 'timeline.select', topicId: topic.id });
                  }}
                >
                  {topic.id === onAirTopicId ? 'В эфире' : 'В эфир'}
                </button>
              )}
              {topic.url && topic.summaryStatus !== 'pending' && (
                <button
                  class="small"
                  title={topic.summaryStatus === 'ready' ? 'Обновить recap' : 'Повторить подготовку recap'}
                  onClick={() => sendAny({ type: 'topic.summary.retry', topicId: topic.id })}
                >
                  ↻ Recap
                </button>
              )}
              <button
                class="small danger"
                title="Удалить тему"
                onClick={() => {
                  if (confirm(`Удалить тему «${topic.title}»?`)) sendAny({ type: 'topic.delete', topicId: topic.id });
                }}
              >
                🗑
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Таймлайн ----------

function TimelinePanel({
  state, send, isUser
}: {
  state: RoomState;
  send: (m: never) => void;
  isUser: boolean;
}) {
  const sendAny = send as (m: unknown) => void;
  if (state.timeline.length === 0) return null;

  const editTime = (entry: TimelineEntry) => {
    if (!isUser || state.readOnly) return;
    const input = prompt('Таймкод (чч:мм:сс):', entry.timecode);
    if (!input) return;
    const parts = input.split(':').map(Number);
    if (parts.some((n) => !Number.isFinite(n))) return;
    const seconds = parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2] : parts[0] * 60 + (parts[1] || 0);
    sendAny({ type: 'timeline.editTime', entryId: entry.id, startMs: seconds * 1000 });
  };

  return (
    <div class="panel">
      <h2>Таймлайн эфира</h2>
      {state.timeline.map((entry) => (
        <div class="timelineItem" key={entry.id}>
          <span class="tc" onClick={() => editTime(entry)} title="Изменить время">
            {entry.timecode}
          </span>
          <span>{entry.title}</span>
          {entry.source === 'adhoc' && <span class="src">авто</span>}
        </div>
      ))}
    </div>
  );
}

// ---------- Стенограмма ----------

// Слияние строк по id (сортировка по возрастанию, без дублей, хвост капается)
function mergeTranscript(prev: TranscriptLine[], incoming: TranscriptLine[]): TranscriptLine[] {
  const byId = new Map<number, TranscriptLine>();
  for (const line of prev) byId.set(line.id, line);
  for (const line of incoming) byId.set(line.id, line);
  const merged = [...byId.values()].sort((a, b) => a.id - b.id);
  return merged.length > 400 ? merged.slice(merged.length - 400) : merged;
}

function TranscriptPanel({ lines }: { lines: TranscriptLine[] }) {
  const [open, setOpen] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);

  // Свежие реплики сверху — скроллим к началу при обновлении
  useEffect(() => {
    if (open) logRef.current?.scrollTo({ top: 0 });
  }, [lines.length, open]);

  return (
    <div class="panel">
      <h2 class="collapsible" onClick={() => setOpen(!open)}>
        Стенограмма <span class="mutedText">{open ? '▾' : '▸'}</span>
      </h2>
      {open && (
        lines.length === 0 ? (
          <p class="mutedText">Расшифровка появится во время записи.</p>
        ) : (
          <div class="transcriptLog" ref={logRef}>
            {[...lines].reverse().map((line) => (
              <div class="transcriptLine" key={line.id}>
                <span class="tc">{formatMs(line.startMs)}</span>
                <span class="who">{line.participantName || 'Спикер'}:</span>
                <span class="text">{line.text}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ---------- Чат ----------

function ChatPanel({ state, send }: { state: RoomState; send: (m: never) => void }) {
  const sendAny = send as (m: unknown) => void;
  const [text, setText] = useState('');
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [state.chat.length]);

  const submit = (event: Event) => {
    event.preventDefault();
    if (!text.trim()) return;
    sendAny({ type: 'chat.send', text });
    setText('');
  };

  return (
    <div class="panel">
      <h2>Чат</h2>
      <div class="chatLog" ref={logRef}>
        {state.chat.map((message) => (
          <div class="chatMsg" key={message.id}>
            <span class="author">{message.author}</span>
            <span class="time">
              {new Date(message.ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <div>{message.text}</div>
          </div>
        ))}
      </div>
      <form class="chatForm" onSubmit={submit}>
        <input type="text" value={text} onInput={(e) => setText(e.currentTarget.value)} placeholder="Сообщение…" />
        <button class="primary" type="submit">
          →
        </button>
      </form>
    </div>
  );
}

// ---------- Архив ----------

function ArchivePanel({ state }: { state: RoomState }) {
  const alive = state.archive.filter((item) => !item.deletedAt);
  const trash = state.archive.filter((item) => item.deletedAt);
  return (
    <div class="panel">
      <h2>Архив выпусков</h2>
      {alive.map((item) => (
        <div class="archiveItem" key={item.id}>
          <div>
            <a
              href={`/episodes/${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/episodes/${item.id}`);
              }}
            >
              {item.title}
            </a>
            <div class="mutedText">
              {item.status === 'finished' ? 'завершён' : item.status} · тем: {item.topicCount}
              {item.hasRecordings ? ' · есть запись' : ''}
            </div>
          </div>
          <div class="userbar">
            <span class="mutedText">
              {item.startedAt ? new Date(item.startedAt).toLocaleDateString('ru-RU') : ''}
            </span>
            {item.status !== 'live' && (
              <button
                class="small danger"
                title="В корзину"
                onClick={() => void deleteEpisodeConfirmed(item.id, item.title, item.id === state.activeEpisodeId)}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      ))}
      {trash.length > 0 && (
        <>
          <h2>Корзина</h2>
          <p class="mutedText">
            Удалённые выпуски: запись и темы сохранены, можно восстановить (если номер свободен) или стереть навсегда.
          </p>
          {trash.map((item) => (
            <div class="archiveItem trash" key={item.id}>
              <div>
                <a
                  href={`/episodes/${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/episodes/${item.id}`);
                  }}
                >
                  {item.title}
                </a>
                <div class="mutedText">
                  удалён {item.deletedAt ? new Date(item.deletedAt).toLocaleString('ru-RU') : ''} · тем: {item.topicCount}
                  {item.hasRecordings ? ' · есть запись' : ''}
                </div>
              </div>
              <div class="userbar">
                <button class="small" onClick={() => void restoreEpisode(item.id, item.title)}>Восстановить</button>
                <button class="small danger" title="Стереть навсегда" onClick={() => void purgeEpisodeConfirmed(item.id, item.title)}>
                  ✕ навсегда
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ---------- Инвайты ----------

interface InviteRow {
  id: number;
  createdAt: string;
  usedAt: string | null;
  createdBy: string;
  usedBy: string | null;
}

function InvitesPanel() {
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [lastUrl, setLastUrl] = useState('');

  const load = () => void api<{ invites: InviteRow[] }>('/api/invites').then((d) => setInvites(d.invites));
  useEffect(load, []);

  const create = async () => {
    const { invite } = await api<{ invite: { inviteUrl: string } }>('/api/invites', { method: 'POST' });
    setLastUrl(invite.inviteUrl);
    await navigator.clipboard.writeText(invite.inviteUrl).catch(() => {});
    load();
  };

  return (
    <div class="panel">
      <h2>Инвайты команды</h2>
      <div class="actions">
        <button class="primary" onClick={() => void create()}>
          Создать инвайт
        </button>
      </div>
      {lastUrl && (
        <p>
          Ссылка (в буфере): <code class="inline">{lastUrl}</code>
        </p>
      )}
      {invites.map((invite) => (
        <div class="archiveItem" key={invite.id}>
          <div>
            #{invite.id} от {invite.createdBy}
            <div class="mutedText">
              {invite.usedAt ? `использован: ${invite.usedBy}` : 'не использован'}
            </div>
          </div>
          {!invite.usedAt && (
            <button
              class="small danger"
              onClick={() => void api(`/api/invites/${invite.id}`, { method: 'DELETE' }).then(load)}
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
